
import { Options, mixins } from 'vue-class-component'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'
import RichMenuSelector from '@/components/rich-menus/selectors/RichMenuSelector.vue'
import { maska } from 'maska'
import { IRichMenu, IRichMenuContent } from '@/utils/types'
import { NumberUtils } from '@/utils/number'
import { constant } from '@/utils/constants'
import { ACTION_RICH_MENU } from '@/store/actions'
import BaseFormMixin from '@/components/common/mixins/BaseFormMixin.vue'
import cloneDeep from 'lodash/cloneDeep'
import { Watch } from 'vue-property-decorator'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, RichMenuSelector, TestModeSelector },
  directives: { maska },
  async beforeRouteLeave() {
    if (!this) {
      return
    }
    const result = await this.checkDraftValue()
    if (result) {
      return true
    } else {
      return false
    }
  },
})
export default class RichMenuSettingForm extends mixins(BaseFormMixin) {
  isTestMode = false
  loading = false
  menuImageUrl = ''

  form: IRichMenu = {
    _id: '',
    name: '',
    app_id: '',
    is_active: false,
    is_test_mode: false,
    prod_detail: {
      chat_bar_text: '',
      rich_menu_content: [
        {
          label: '',
          rich_menu_alias_id: NumberUtils.uniqueId(),
          size: {
            width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
            height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
          },
          tappable_area: [],
          image_url: '',
        },
      ],
    },
    test_detail: {
      chat_bar_text: '',
      rich_menu_content: [
        {
          label: '',
          rich_menu_alias_id: NumberUtils.uniqueId(),
          size: {
            width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
            height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
          },
          tappable_area: [],
          image_url: '',
        },
      ],
    },
  }

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  get selectedRichMenuId() {
    return this.$route.params.rich_menu_id || ''
  }

  get actionType() {
    return this.$route.params.action_type || ''
  }

  get requiredRule() {
    const requiredRule = {
      name: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.rich_menu.name'),
      }),

      chatBarText: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.rich_menu.chat_bar_text'),
      }),
      messageImage: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.messages.image'),
      }),
    }
    return requiredRule
  }

  onDetailCopy() {
    if (this.isTestMode) {
      this.form.test_detail = this.form.prod_detail
    } else {
      this.form.prod_detail = this.form.test_detail
    }
  }

  onCancel() {
    this.goto('rich_menus', { app_id: this.selectedAppId })
  }

  onSubmit(isBack = true) {
    this.$refs.formRef
      .validate()
      .then(async (success: boolean) => {
        if (!success) {
          return
        }

        await this.doSave(isBack)
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  async doSave(isBack = true) {
    this.loading = true
    const { dispatch } = this.$store
    let item = null
    if (!this.form._id) {
      this.form.is_active = false
      this.form.app_id = this.selectedAppId
      this.form.test_detail = this.form.prod_detail
      item = await dispatch(ACTION_RICH_MENU.ADD, {
        ...this.form,
      })
    } else {
      item = await dispatch(ACTION_RICH_MENU.UPDATE, {
        ...this.form,
      })
    }

    if (item) {
      this.loading = false
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })

      this.initForm = cloneDeep(this.parentForm)
      if (isBack) {
        this.onCancel()
      } else {
        this.$refs.richMenuSelector.updateOriginalMenus()
      }
    } else {
      this.loading = false
    }
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  @Watch('isTestMode', { immediate: true })
  onPreview(data: IRichMenuContent) {
    this.menuImageUrl = data.image_url ?? ''
  }

  @Watch('actionType', { immediate: true })
  @Watch('selectedRichMenuId', { immediate: true })
  async fetchRichMenu() {
    if (!this.selectedAppId) {
      return
    }

    if (this.selectedRichMenuId !== '') {
      const data: IRichMenu = await this.$store.dispatch(ACTION_RICH_MENU.LOAD_ITEM, this.selectedRichMenuId)
      if (data) {
        this.form = { ...this.form, ...data }
        if (this.actionType === constant.ACTION_TYPE.COPY) {
          this.form._id = ''
          this.form.name = this.form.name + ' Copy'
          this.form.is_test_mode = false
          this.form.is_active = false
          this.form.prod_rich_menu_alias_ids = []
          this.form.prod_rich_menu_ids = []
          this.form.test_rich_menu_alias_ids = []
          this.form.test_rich_menu_ids = []
          if (this.form.prod_detail?.rich_menu_content) {
            for (const item of this.form.prod_detail?.rich_menu_content) {
              item.rich_menu_alias_id = NumberUtils.uniqueId()
            }
          }

          if (this.form.test_detail?.rich_menu_content) {
            for (const item of this.form.test_detail?.rich_menu_content) {
              item.rich_menu_alias_id = NumberUtils.uniqueId()
            }
          }
        }
      }
      this.parentForm = this.form as any
      this.initForm = cloneDeep(this.parentForm)
    }
  }

  refetchMenu(data: IRichMenuContent) {
    setTimeout(() => {
      if (this.isTestMode) {
        if (this.form.test_detail?.rich_menu_content) {
          this.form.test_detail.rich_menu_content.push(data)
        }
      } else {
        if (this.form.prod_detail?.rich_menu_content) {
          this.form.prod_detail.rich_menu_content.push(data)
        }
      }
    }, 500)
  }

  async created() {
    await this.fetchRichMenu()

    if (this.selectedRichMenuId === '') {
      this.$nextTick(() => {
        this.$refs.formRef.validate()
      })
    }
  }
}
